import { useContext, useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { inject, observer, PropTypes as MobXPropTypes } from 'mobx-react';
import Table from 'react-bootstrap/Table';
import queryString from 'query-string';
import EmailValidationRulesFilterBar from './components/EmailValidationRulesFilterBar';
import { EmailValidationRulesTableHeadRow } from './components/EmailValidationRulesTableHeadRow';
import { EmailValidationRulesTableRowContent } from './components/EmailValidationRulesTableRowContent';
import PopUpCreateEmailValidationRuleScheme from './components/PopUpCreateEmailValidationRuleScheme';
import { DIRECTION, MENU_DIRECTION } from 'components/constants';
import Loader from 'components/Loader';
import i18nContext from 'components/i18n-context';
import EmptyTableContentPlug from 'components/EmptyTableContentPlug';
import Pagination from 'components/Pagination/Pagination';
import Button from 'uikit/Button/Button';
import { Container } from 'uikit/Container/Container';
import { PopUp } from 'uikit/PopUp/PopUp';
import './EmailValidationRulesPage.scss';

const popUpInitialData = {
  isShowPopUp: false,
  onCancel: () => {},
  onConfirm: () => {}
};

const EmailValidationRulesPage = ({ emailValidationRulesStore }) => {
  const i18n = useContext(i18nContext);
  const location = useLocation();
  const navigate = useNavigate();
  const [popUpData, setPopUpData] = useState(popUpInitialData);

  const updateFiltersInUrl = (filters, pagination) => {
    const queryParams = {
      page: pagination.page,
      size: pagination.size,
      sort_direction: pagination.direction
    };

    if (filters.search_text) {
      queryParams.search_text = filters.search_text;
    }

    if (filters.action) {
      queryParams.action = filters.action;
    }

    if (pagination.sortBy) {
      queryParams.sort_column = pagination.sortBy;
    }

    const searchParams = queryString.stringify(queryParams);
    navigate({ search: `?${searchParams}` }, { replace: true });
  };

  const loadFiltersFromUrl = () => {
    const params = queryString.parse(location.search);
    emailValidationRulesStore.setFiltersFromUrl(params);
  };

  useEffect(() => {
    loadFiltersFromUrl();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!emailValidationRulesStore.isInitialized) {
      emailValidationRulesStore.getRulesList();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [emailValidationRulesStore.isInitialized]);

  useEffect(() => {
    if (emailValidationRulesStore.isInitialized) {
      emailValidationRulesStore.resetEmailValidationRulesStore();
      loadFiltersFromUrl();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.pathname]);

  useEffect(() => {
    updateFiltersInUrl(emailValidationRulesStore.filters, emailValidationRulesStore.pagination);

    if (emailValidationRulesStore.isInitialized) {
      emailValidationRulesStore.getRulesList();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    emailValidationRulesStore.filters.search_text,
    emailValidationRulesStore.filters.action,
    emailValidationRulesStore.pagination.size,
    emailValidationRulesStore.pagination.page,
    emailValidationRulesStore.sortColumn.sort_column,
    emailValidationRulesStore.sortColumn.sort_direction
  ]);

  useEffect(() => {
    if (emailValidationRulesStore.isEmailValidationRuleSuccessfullyDeleted) {
      emailValidationRulesStore.getRulesList();
      emailValidationRulesStore.setIsEmailValidationRuleSuccessfullyDeleted(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [emailValidationRulesStore.isEmailValidationRuleSuccessfullyDeleted]);

  const handleSetCurrentPage = (pageNumber) => {
    emailValidationRulesStore.setEmailValidationRulesPage(pageNumber);
  };

  const handleSetPageSize = (size) => {
    emailValidationRulesStore.setEmailValidationRulesPageSize(size);
  };

  const handleOnSortTable = (newSortBy) => {
    emailValidationRulesStore.setSortData({
      sortBy: newSortBy,
      direction:
        emailValidationRulesStore.sortColumn.sort_column !== newSortBy
          ? DIRECTION.ASC
          : emailValidationRulesStore.sortColumn.sort_direction === DIRECTION.ASC
            ? DIRECTION.DESC
            : DIRECTION.ASC
    });
  };

  const resetPopUpData = () => {
    setPopUpData(popUpInitialData);
  };

  const handleCreateEmailValidationRule = async () => {
    setPopUpData({
      isShowPopUp: true,
      onConfirm: async (data) => {
        await emailValidationRulesStore.createEmailValidationRule(data);
        resetPopUpData();
      },
      onCancel: resetPopUpData
    });
  };

  if (!emailValidationRulesStore.isInitialized) {
    return <Loader className={'application-loader'} />;
  }

  return (
    <Container className={'email-validation-rules-container'} header={i18n.getMessage('emailValidationRules.header')}>
      <div className={'email-validation-rules-actions-wrapper'}>
        <Button
          className={'email-validation-rules-button'}
          size={'medium'}
          type={'primary'}
          onClick={handleCreateEmailValidationRule}
        >
          {i18n.getMessage('emailValidationRules.button.addNewRule')}
        </Button>
      </div>

      <EmailValidationRulesFilterBar
        isInitialized={emailValidationRulesStore.isInitialized}
        filters={emailValidationRulesStore.filters}
        handleSetFilters={emailValidationRulesStore.setFilter}
        totalElements={emailValidationRulesStore.totalElements}
      />

      {emailValidationRulesStore.rulesList?.length === 0 ? (
        <EmptyTableContentPlug
          headerText={i18n.getMessage('emailValidationRules.table.emptyPlug.header')}
          descriptionText={i18n.getMessage('emailValidationRules.table.emptyPlug.description')}
        />
      ) : (
        <>
          <Pagination
            isShowPaginationSize={true}
            paginationSize={emailValidationRulesStore.pagination.size}
            handleChangePaginationSize={handleSetPageSize}
            isLoading={emailValidationRulesStore.isLoading}
            currentPage={emailValidationRulesStore.pagination.page}
            numberOfPages={emailValidationRulesStore.pagination.totalPages}
            setCurrentPage={handleSetCurrentPage}
          />

          <Table responsive>
            <thead>
              <EmailValidationRulesTableHeadRow
                handleOnSortTable={handleOnSortTable}
                sortBy={emailValidationRulesStore.sortColumn.sort_column}
                direction={emailValidationRulesStore.sortColumn.sort_direction}
              />
            </thead>
            <tbody>
              {emailValidationRulesStore?.rulesList?.map((emailValidationRule, index) => (
                <EmailValidationRulesTableRowContent
                  key={index}
                  type={'list'}
                  isLoading={emailValidationRulesStore.isLoading}
                  emailValidationRuleData={emailValidationRule}
                  handleDeleteEmailValidationRule={emailValidationRulesStore.deleteEmailValidationRule}
                />
              ))}
            </tbody>
          </Table>
          <Pagination
            isShowPaginationSize={true}
            paginationSize={emailValidationRulesStore.pagination.size}
            handleChangePaginationSize={handleSetPageSize}
            isLoading={emailValidationRulesStore.isLoading}
            currentPage={emailValidationRulesStore.pagination.page}
            numberOfPages={emailValidationRulesStore.pagination.totalPages}
            setCurrentPage={handleSetCurrentPage}
            menuDirection={MENU_DIRECTION.UP}
          />
        </>
      )}

      <PopUp alignOnCenter show={popUpData.isShowPopUp}>
        <PopUpCreateEmailValidationRuleScheme
          isLoading={emailValidationRulesStore.isLoading}
          handleClose={popUpData.onCancel}
          handleCreateEmailValidationRule={popUpData.onConfirm}
        />
      </PopUp>
    </Container>
  );
};

EmailValidationRulesPage.propTypes = {
  emailValidationRulesStore: MobXPropTypes.observableObject
};

export default inject('emailValidationRulesStore')(observer(EmailValidationRulesPage));
